import { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Login from '@/screens/Login';
import Projects from '@/screens/Projects';
import ProjectDashboard from '@/screens/ProjectDashboard';
import Admin from '@/screens/Admin';
import AcceptInvite from '@/screens/AcceptInvite';
import JoinProject from '@/screens/JoinProject';
import ResetPassword from '@/screens/ResetPassword';
import { Spinner } from '@/components/Spinner';
import { ToastContainer } from '@/components/ToastContainer';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { CommandPalette } from '@/components/CommandPalette';
import { useAuth } from '@/state/auth';

// Маршруты SPA.
// Sprint 1 — Login + Projects.
// Sprint 2 — ProjectDashboard + Remarks + RemarkDetail + 4 модала + Toast + Confirm + AI panel.
// Sprint 3 — Documents / Integrity / Query / Settings / GIP-split.
// Sprint 4 — Admin SPA + Command Palette.

export default function App() {
  const bootstrap = useAuth((s) => s.bootstrap);
  const loading = useAuth((s) => s.loading);
  const user = useAuth((s) => s.user);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size={32} className="text-muted" />
      </div>
    );
  }

  return (
    <>
      <Routes>
        <Route path="/" element={<Navigate to="/projects" replace />} />
        <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
        <Route path="/invite/:token" element={<AcceptInvite />} />
        <Route path="/join/:token" element={<JoinProject />} />
        <Route path="/reset/:token" element={<ResetPassword />} />
        <Route path="/projects" element={<Protected><Projects /></Protected>} />
        <Route path="/project/:id" element={<Protected><ProjectDashboard /></Protected>} />
        <Route path="/admin/*" element={<Protected requireAdmin><Admin /></Protected>} />
        <Route path="*" element={<PlaceholderScreen title="Страница не найдена" />} />
      </Routes>
      <ToastContainer />
      <ConfirmDialog />
      {user && <CommandPalette />}
    </>
  );
}

function Protected({ children, requireAdmin = false }: { children: React.ReactElement; requireAdmin?: boolean }) {
  const user = useAuth((s) => s.user);
  const loc = useLocation();
  if (!user) return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  if (requireAdmin && user.default_role !== 'admin') return <Navigate to="/projects" replace />;
  return children;
}

function PublicOnly({ children }: { children: React.ReactElement }) {
  const user = useAuth((s) => s.user);
  if (user) return <Navigate to="/projects" replace />;
  return children;
}

function PlaceholderScreen({ title }: { title: string }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-surface">
      <div className="text-center">
        <h1 className="font-heading text-ink" style={{ fontSize: 28, fontWeight: 600, letterSpacing: '-0.01em' }}>{title}</h1>
        <p className="text-muted mt-2" style={{ fontSize: 14 }}>
          Перенести из <code className="font-mono">static/app-v2-prototype.html</code>
        </p>
      </div>
    </div>
  );
}
